import React, { useState, useEffect } from "react";
import { servicesAPI, ticketAPI, logsAPI, handleAPIError } from "../services/api";
import FAQManagement from "./FAQManagement";
import BulkActionBar from "./BulkActionBar";
import BulkDeleteConfirmDialog from "./BulkDeleteConfirmDialog";

const AdminPanel = () => {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [finalServices, setFinalServices] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [newSubcategory, setNewSubcategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState("");
  const [editSubcategoryName, setEditSubcategoryName] = useState("");

  // New state for two-option creation
  const [categoryIsLeafLevel, setCategoryIsLeafLevel] = useState(false);
  const [subcategoryIsLeafLevel, setSubcategoryIsLeafLevel] = useState(false);
  const [categoryDescription, setCategoryDescription] = useState("");
  const [categoryDirectUrl, setCategoryDirectUrl] = useState("");
  const [subcategoryDescription, setSubcategoryDescription] = useState("");
  const [subcategoryDirectUrl, setSubcategoryDirectUrl] = useState("");

  // State to track if current level is a leaf level
  const [isCurrentLevelLeaf, setIsCurrentLevelLeaf] = useState(false);

  // State for editing services
  const [editingService, setEditingService] = useState(null);
  const [editServiceData, setEditServiceData] = useState({
    description: "",
    direct_url: "",
  });

  // Tab state
  const [activeTab, setActiveTab] = useState("services");

  // Logs tab state
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState("");
  const [logsSessionFilter, setLogsSessionFilter] = useState("");
  const [logsTokenFilter, setLogsTokenFilter] = useState("");
  const [logsLimit, setLogsLimit] = useState(200);
  const [logsDatePreset, setLogsDatePreset] = useState("all"); // all | today | week | month | custom
  const [logsFromDate, setLogsFromDate] = useState("");
  const [logsToDate, setLogsToDate] = useState("");
  const [logsExporting, setLogsExporting] = useState(false);

  // Tickets tab state
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState("");
  const [ticketsExporting, setTicketsExporting] = useState(false);
  const [ticketsDatePreset, setTicketsDatePreset] = useState("all");
  const [ticketsFromDate, setTicketsFromDate] = useState("");
  const [ticketsToDate, setTicketsToDate] = useState("");

  // Bulk upload state
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [showTemplate, setShowTemplate] = useState(false);
  const [templateData, setTemplateData] = useState(null);

  // Excel-style editor states
  const [showExcelEditor, setShowExcelEditor] = useState(false);
  const [excelEditorMinimized, setExcelEditorMinimized] = useState(false);
  const [excelData, setExcelData] = useState([
    {
      category: "",
      sub_category_l0: "",
      sub_category_l1: "",
      sub_category_l2: "",
      sub_category_l3: "",
      sub_category_l4: "",
      sub_category_l5: "",
      description: "",
      direct_url: "",
    },
  ]);
  const [excelEditing, setExcelEditing] = useState(false);

  // Recycle bin states
  const [showRecycleBin, setShowRecycleBin] = useState(false);
  const [recycleBinMinimized, setRecycleBinMinimized] = useState(false);
  const [deletedServices, setDeletedServices] = useState([]);
  const [recycleBinLoading, setRecycleBinLoading] = useState(false);

  // Bulk delete states
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [selectedDeletedServices, setSelectedDeletedServices] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleteType, setBulkDeleteType] = useState(""); // "categories", "subcategories", "restore", "permanent"
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Load categories on component mount
  useEffect(() => {
    loadCategories();
  }, []);

  const getLogsDateRange = () => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const d = String(today.getDate()).padStart(2, "0");
    const todayStr = `${y}-${m}-${d}`;
    if (logsDatePreset === "today") return { from_date: todayStr, to_date: todayStr };
    if (logsDatePreset === "week") {
      const from = new Date(today);
      from.setDate(from.getDate() - 6);
      const fy = from.getFullYear();
      const fm = String(from.getMonth() + 1).padStart(2, "0");
      const fd = String(from.getDate()).padStart(2, "0");
      return { from_date: `${fy}-${fm}-${fd}`, to_date: todayStr };
    }
    if (logsDatePreset === "month") {
      const from = new Date(today);
      from.setDate(from.getDate() - 29);
      const fy = from.getFullYear();
      const fm = String(from.getMonth() + 1).padStart(2, "0");
      const fd = String(from.getDate()).padStart(2, "0");
      return { from_date: `${fy}-${fm}-${fd}`, to_date: todayStr };
    }
    if (logsDatePreset === "custom" && logsFromDate && logsToDate)
      return { from_date: logsFromDate, to_date: logsToDate };
    return {};
  };

  const loadLogs = async () => {
    setLogsLoading(true);
    setLogsError("");
    try {
      const params = { limit: logsLimit };
      if (logsSessionFilter.trim()) params.session_id = logsSessionFilter.trim();
      if (logsTokenFilter.trim()) params.token = logsTokenFilter.trim();
      const range = getLogsDateRange();
      if (range.from_date) params.from_date = range.from_date;
      if (range.to_date) params.to_date = range.to_date;
      const data = await logsAPI.getConversationLogs(params);
      setLogs(data.logs || []);
    } catch (err) {
      setLogsError(handleAPIError(err));
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const formatLogTimestamp = (isoStr) => {
    if (!isoStr) return "—";
    try {
      const dt = new Date(isoStr);
      return dt.toLocaleString(undefined, {
        dateStyle: "short",
        timeStyle: "medium",
      });
    } catch {
      return isoStr;
    }
  };

  const exportLogsToExcel = async () => {
    setLogsExporting(true);
    setLogsError("");
    try {
      const params = { limit: 5000 };
      if (logsSessionFilter.trim()) params.session_id = logsSessionFilter.trim();
      if (logsTokenFilter.trim()) params.token = logsTokenFilter.trim();
      const range = getLogsDateRange();
      if (range.from_date) params.from_date = range.from_date;
      if (range.to_date) params.to_date = range.to_date;
      const data = await logsAPI.getConversationLogs(params);
      const rows = data.logs || [];
      const escapeCsv = (v) => {
        const s = v == null ? "" : String(v);
        if (s.includes('"') || s.includes(",") || s.includes("\n")) return `"${s.replace(/"/g, '""')}"`;
        return s;
      };
      const headers = ["Timestamp", "Session ID", "Token", "Role", "Content"];
      const csvRows = [headers.map(escapeCsv).join(",")];
      rows.forEach((log) => {
        csvRows.push(
          [
            log.created_at || "",
            log.session_id || "",
            log.token || "",
            log.role || "",
            log.content || "",
          ].map(escapeCsv).join(",")
        );
      });
      const csv = csvRows.join("\n");
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `conversation-logs-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setLogsError(handleAPIError(err));
    } finally {
      setLogsExporting(false);
    }
  };

  const loadTickets = async () => {
    setTicketsLoading(true);
    setTicketsError("");
    try {
      const data = await ticketAPI.listTickets();
      setTickets(data.tickets || []);
    } catch (err) {
      setTicketsError(handleAPIError(err));
      setTickets([]);
    } finally {
      setTicketsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "logs") loadLogs();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "tickets") loadTickets();
  }, [activeTab]);

  const exportTicketsToExcel = () => {
    setTicketsExporting(true);
    setTicketsError("");
    try {
      const escapeCsv = (v) => {
        const s = v == null ? "" : String(v);
        if (s.includes('"') || s.includes(",") || s.includes("\n")) return `"${s.replace(/"/g, '""')}"`;
        return s;
      };
      const exportedAt = new Date().toLocaleString(undefined, { dateStyle: "short", timeStyle: "medium" });
      const headers = ["Exported at", "Session ID", "Token", "Domain", "Sub Cat Head", "Sub Category", "Query"];
      const csvRows = [headers.map(escapeCsv).join(",")];
      tickets.forEach((t) => {
        csvRows.push(
          [
            exportedAt,
            t.session_id || "",
            t.token || "",
            t.domain || "",
            t.sub_cat_head || "",
            t.sub_category || "",
            t.query || "",
          ].map(escapeCsv).join(",")
        );
      });
      const csv = csvRows.join("\n");
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tickets-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setTicketsError(handleAPIError(err));
    } finally {
      setTicketsExporting(false);
    }
  };

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await servicesAPI.getCategories();
      setCategories(response.categories || []);
      setError("");
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const loadAllServices = async (path = []) => {
    try {
      const params = {};
      if (path[0]) params.category = path[0];
      if (path[1]) params.sub_category_l0 = path[1];
      if (path[2]) params.sub_category_l1 = path[2];
      if (path[3]) params.sub_category_l2 = path[3];
      if (path[4]) params.sub_category_l3 = path[4];
      if (path[5]) params.sub_category_l4 = path[5];
      if (path[6]) params.sub_category_l5 = path[6];

      const response = await servicesAPI.listServices(params);
      setAllServices(response);
    } catch (err) {
      console.error("Error loading all services:", err);
    }
  };

  const loadSubcategories = async (path = []) => {
    try {
      setLoading(true);
      const params = {};
      if (path[0]) params.category = path[0];
      if (path[1]) params.sub_category_l0 = path[1];
      if (path[2]) params.sub_category_l1 = path[2];
      if (path[3]) params.sub_category_l2 = path[3];
      if (path[4]) params.sub_category_l3 = path[4];
      if (path[5]) params.sub_category_l4 = path[5];
      if (path[6]) params.sub_category_l5 = path[6];

      const response = await servicesAPI.getSubcategories(params);
      setSubcategories(response.subcategories || []);
      setCurrentPath(response.current_path || []);

      // Load all services and final services at this level
      await Promise.all([loadAllServices(path), loadFinalServices(path)]);

      setError("");
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const loadFinalServices = async (path = []) => {
    try {
      const params = {};
      if (path[0]) params.category = path[0];
      if (path[1]) params.sub_category_l0 = path[1];
      if (path[2]) params.sub_category_l1 = path[2];
      if (path[3]) params.sub_category_l2 = path[3];
      if (path[4]) params.sub_category_l3 = path[4];
      if (path[5]) params.sub_category_l4 = path[5];
      if (path[6]) params.sub_category_l5 = path[6];

      const response = await servicesAPI.listServices(params);
      // Filter services that have description or URL at the current path level
      const servicesWithDetails = response.filter((service) => {
        // Check if this service matches the current path exactly
        const servicePath = [
          service.category,
          service.sub_category_l0,
          service.sub_category_l1,
          service.sub_category_l2,
          service.sub_category_l3,
          service.sub_category_l4,
          service.sub_category_l5,
        ].filter(Boolean);

        // Only show services that match the current path and have description/URL
        return (
          servicePath.length === path.length &&
          path.every((item, index) => servicePath[index] === item) &&
          (service.description || service.direct_url)
        );
      });
      setFinalServices(servicesWithDetails);

      // Check if current level is a leaf level (has final services with descriptions/URLs)
      const isLeafLevel = servicesWithDetails.length > 0;
      setIsCurrentLevelLeaf(isLeafLevel);

      // If we're at a leaf level, automatically set subcategory creation to Leaf Level
      if (isLeafLevel) {
        setSubcategoryIsLeafLevel(true);
      }
    } catch (err) {
      console.error("Error loading final services:", err);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) return;

    // Validate description and URL requirements only for leaf level
    if (
      categoryIsLeafLevel &&
      (!categoryDescription.trim() || !categoryDirectUrl.trim())
    ) {
      setError(
        "Description and Direct URL are required for leaf level categories",
      );
      return;
    }

    try {
      setLoading(true);
      const request = {
        category_name: newCategory.trim(),
        is_leaf_level: categoryIsLeafLevel,
        description: categoryIsLeafLevel ? categoryDescription.trim() : null,
        direct_url: categoryIsLeafLevel ? categoryDirectUrl.trim() : null,
      };
      await servicesAPI.createCategory(request);

      // Reset form
      setNewCategory("");
      setCategoryIsLeafLevel(false);
      setCategoryDescription("");
      setCategoryDirectUrl("");

      await loadCategories();
      setError("");
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategory = (categoryName) => {
    setEditingCategory(categoryName);
    setEditCategoryName(categoryName);
  };

  const handleUpdateCategory = async () => {
    if (
      !editCategoryName.trim() ||
      editCategoryName.trim() === editingCategory
    ) {
      setEditingCategory(null);
      setEditCategoryName("");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to update category "${editingCategory}" to "${editCategoryName.trim()}"?`,
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      await servicesAPI.updateCategory(
        editingCategory,
        editCategoryName.trim(),
      );
      setEditingCategory(null);
      setEditCategoryName("");
      await loadCategories();
      if (currentPath[0] === editingCategory) {
        setCurrentPath([]);
        setSubcategories([]);
        setFinalServices([]);
        setAllServices([]);
      }
      setError("");
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryName) => {
    if (
      !window.confirm(
        `Are you sure you want to delete category "${categoryName}" and all its services?`,
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      await servicesAPI.deleteCategory(categoryName);
      await loadCategories();
      if (currentPath[0] === categoryName) {
        setCurrentPath([]);
        setSubcategories([]);
        setFinalServices([]);
        setAllServices([]);
      }
      setError("");
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubcategory = async (e) => {
    e.preventDefault();
    if (!newSubcategory.trim()) return;

    // Validate leaf level requirements
    if (
      subcategoryIsLeafLevel &&
      (!subcategoryDescription.trim() || !subcategoryDirectUrl.trim())
    ) {
      setError(
        "Description and Direct URL are required for leaf level subcategories",
      );
      return;
    }

    const currentLevel = getCurrentLevel();
    const levelName = getLevelName(currentLevel);
    const creationType = subcategoryIsLeafLevel
      ? "leaf level service"
      : "intermediate level";

    if (
      !window.confirm(
        `Are you sure you want to create subcategory "${newSubcategory.trim()}" as a ${creationType} at level ${levelName}?`,
      )
    ) {
      return;
    }

    try {
      setLoading(true);

      // For levels beyond L3, use the direct services endpoint
      if (currentLevel >= 3) {
        const serviceData = {
          category: currentPath[0],
          sub_category_l0: currentPath[1] || null,
          sub_category_l1: currentPath[2] || null,
          sub_category_l2: currentPath[3] || null,
          sub_category_l3: currentPath[4] || null,
          sub_category_l4: currentLevel === 4 ? newSubcategory.trim() : null,
          sub_category_l5: currentLevel === 5 ? newSubcategory.trim() : null,
          description: subcategoryIsLeafLevel
            ? subcategoryDescription.trim()
            : null,
          direct_url: subcategoryIsLeafLevel
            ? subcategoryDirectUrl.trim()
            : null,
        };

        await servicesAPI.createService(serviceData);
      } else {
        // For levels 0-3, use the subcategories endpoint
        const request = {
          subcategory_name: newSubcategory.trim(),
          current_path: currentPath,
          is_leaf_level: subcategoryIsLeafLevel,
          description: subcategoryIsLeafLevel
            ? subcategoryDescription.trim()
            : null,
          direct_url: subcategoryIsLeafLevel
            ? subcategoryDirectUrl.trim()
            : null,
        };
        await servicesAPI.createSubcategory(request);
      }

      // Reset form
      setNewSubcategory("");
      setSubcategoryIsLeafLevel(false);
      setSubcategoryDescription("");
      setSubcategoryDirectUrl("");

      await loadSubcategories(currentPath);
      setError("");
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubcategory = (subcategoryName) => {
    setEditingSubcategory(subcategoryName);
    setEditSubcategoryName(subcategoryName);
  };

  const handleUpdateSubcategory = async () => {
    if (
      !editSubcategoryName.trim() ||
      editSubcategoryName.trim() === editingSubcategory
    ) {
      setEditingSubcategory(null);
      setEditSubcategoryName("");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to update subcategory "${editingSubcategory}" to "${editSubcategoryName.trim()}"?`,
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      await servicesAPI.updateSubcategory(
        currentPath,
        editingSubcategory,
        editSubcategoryName.trim(),
      );
      setEditingSubcategory(null);
      setEditSubcategoryName("");
      await loadSubcategories(currentPath);
      setError("");
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubcategory = async (subcategoryName) => {
    if (
      !window.confirm(
        `Are you sure you want to delete subcategory "${subcategoryName}" and all its services?`,
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      const currentLevel = getCurrentLevel();

      // For levels beyond L3, use the direct services endpoint
      if (currentLevel >= 3) {
        const serviceToDelete = allServices.find((service) => {
          const serviceLevel = getServiceLevel(service);
          return (
            serviceLevel === currentLevel &&
            getServiceNameAtLevel(service, currentLevel) === subcategoryName
          );
        });

        if (serviceToDelete) {
          await servicesAPI.deleteService(serviceToDelete.id);
        }
      } else {
        // For levels 0-3, use the subcategories endpoint
        await servicesAPI.deleteSubcategory(currentPath, subcategoryName);
      }

      await loadSubcategories(currentPath);
      setError("");
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (
      !window.confirm(
        `Are you sure you want to delete service with ID ${serviceId}?`,
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      await servicesAPI.deleteService(serviceId);
      await loadSubcategories(currentPath); // Reload all data for the current path
      setError("");
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // BULK DELETE HANDLERS
  // ============================================================================

  // Handle category selection for bulk delete
  const handleSelectCategory = (categoryName) => {
    setSelectedCategories((prev) => {
      if (prev.includes(categoryName)) {
        return prev.filter((c) => c !== categoryName);
      } else {
        return [...prev, categoryName];
      }
    });
  };

  // Handle select all categories
  const handleSelectAllCategories = () => {
    if (selectedCategories.length === categories.length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories([...categories]);
    }
  };

  // Handle subcategory selection for bulk delete
  const handleSelectSubcategory = (subcategoryName) => {
    setSelectedSubcategories((prev) => {
      const item = { current_path: currentPath, subcategory_name: subcategoryName };
      const exists = prev.some(
        (s) =>
          s.subcategory_name === subcategoryName &&
          JSON.stringify(s.current_path) === JSON.stringify(currentPath)
      );

      if (exists) {
        return prev.filter(
          (s) =>
            !(
              s.subcategory_name === subcategoryName &&
              JSON.stringify(s.current_path) === JSON.stringify(currentPath)
            )
        );
      } else {
        return [...prev, item];
      }
    });
  };

  // Handle select all subcategories
  const handleSelectAllSubcategories = () => {
    const currentSubcats = subcategories.map((sub) => ({
      current_path: currentPath,
      subcategory_name: sub,
    }));

    const allSelected = currentSubcats.every((item) =>
      selectedSubcategories.some(
        (s) =>
          s.subcategory_name === item.subcategory_name &&
          JSON.stringify(s.current_path) === JSON.stringify(item.current_path)
      )
    );

    if (allSelected) {
      // Remove all current subcategories from selection
      setSelectedSubcategories((prev) =>
        prev.filter(
          (s) => JSON.stringify(s.current_path) !== JSON.stringify(currentPath)
        )
      );
    } else {
      // Add all current subcategories to selection
      setSelectedSubcategories((prev) => {
        const filtered = prev.filter(
          (s) => JSON.stringify(s.current_path) !== JSON.stringify(currentPath)
        );
        return [...filtered, ...currentSubcats];
      });
    }
  };

  // Handle deleted service selection for bulk operations
  const handleSelectDeletedService = (serviceId) => {
    setSelectedDeletedServices((prev) => {
      if (prev.includes(serviceId)) {
        return prev.filter((id) => id !== serviceId);
      } else {
        return [...prev, serviceId];
      }
    });
  };

  // Handle select all deleted services
  const handleSelectAllDeletedServices = () => {
    if (selectedDeletedServices.length === deletedServices.length) {
      setSelectedDeletedServices([]);
    } else {
      setSelectedDeletedServices(deletedServices.map((s) => s.id));
    }
  };

  // Open bulk delete confirmation dialog
  const openBulkDeleteConfirm = (type) => {
    setBulkDeleteType(type);
    setShowBulkDeleteConfirm(true);
  };

  // Cancel bulk delete
  const cancelBulkDelete = () => {
    setShowBulkDeleteConfirm(false);
    setBulkDeleteType("");
  };

  // Confirm and execute bulk delete
  const confirmBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      let response;

      switch (bulkDeleteType) {
        case "categories":
          response = await servicesAPI.bulkDeleteCategories(selectedCategories);
          setSelectedCategories([]);
          await loadCategories();
          setCurrentPath([]);
          setSubcategories([]);
          setFinalServices([]);
          setAllServices([]);
          alert(
            `Successfully deleted ${response.deleted_count} services from ${response.breakdown.categories_deleted} categories`
          );
          break;

        case "subcategories":
          response = await servicesAPI.bulkDeleteSubcategories(selectedSubcategories);
          setSelectedSubcategories([]);
          await loadSubcategories(currentPath);
          alert(
            `Successfully deleted ${response.deleted_count} services from ${response.breakdown.items_deleted} subcategories`
          );
          break;

        case "restore":
          response = await servicesAPI.bulkRestoreServices(selectedDeletedServices);
          setSelectedDeletedServices([]);
          await loadDeletedServices();
          alert(
            `Restored ${response.success_count} services. ${response.failed_count > 0 ? `Failed: ${response.failed_count}` : ""}`
          );
          break;

        case "permanent":
          response = await servicesAPI.bulkPermanentDeleteServices(
            selectedDeletedServices
          );
          setSelectedDeletedServices([]);
          await loadDeletedServices();
          alert(
            `Permanently deleted ${response.success_count} services. ${response.failed_count > 0 ? `Failed: ${response.failed_count}` : ""}`
          );
          break;

        default:
          break;
      }

      setError("");
    } catch (err) {
      setError(handleAPIError(err));
      alert(`Error: ${handleAPIError(err)}`);
    } finally {
      setBulkDeleting(false);
      setShowBulkDeleteConfirm(false);
      setBulkDeleteType("");
    }
  };

  // Cancel bulk selection
  const cancelBulkSelection = (type) => {
    switch (type) {
      case "categories":
        setSelectedCategories([]);
        break;
      case "subcategories":
        setSelectedSubcategories([]);
        break;
      case "deleted":
        setSelectedDeletedServices([]);
        break;
      default:
        break;
    }
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setEditServiceData({
      description: service.description || "",
      direct_url: service.direct_url || "",
    });
  };

  const handleUpdateService = async () => {
    if (
      !editServiceData.description.trim() ||
      !editServiceData.direct_url.trim()
    ) {
      setError("Description and Direct URL are required for services");
      return;
    }

    try {
      setLoading(true);
      await servicesAPI.updateService(editingService.id, {
        description: editServiceData.description.trim(),
        direct_url: editServiceData.direct_url.trim(),
      });

      setEditingService(null);
      setEditServiceData({ description: "", direct_url: "" });
      await loadSubcategories(currentPath);
      setError("");
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEditService = () => {
    setEditingService(null);
    setEditServiceData({ description: "", direct_url: "" });
  };

  // Bulk upload functions
  const handleDownloadTemplate = async () => {
    try {
      setLoading(true);
      const data = await servicesAPI.downloadTemplate();
      setTemplateData(data);
      setShowTemplate(true);
      setError("");
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      setError("Please select a file to upload");
      return;
    }

    try {
      setUploading(true);
      setError("");

      const formData = new FormData();
      formData.append("file", uploadFile);

      const result = await servicesAPI.bulkUploadServices(formData);
      setUploadResult(result);

      // Reload data after successful upload
      await loadCategories();
      if (currentPath.length > 0) {
        await loadSubcategories(currentPath);
      }

      // Reset form
      setUploadFile(null);
      setShowBulkUpload(false);
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "text/csv") {
      setUploadFile(file);
      setError("");
    } else {
      setError("Please select a valid CSV file");
      setUploadFile(null);
    }
  };

  // Excel-style editor functions
  const addExcelRow = () => {
    setExcelData([
      ...excelData,
      {
        category: "",
        sub_category_l0: "",
        sub_category_l1: "",
        sub_category_l2: "",
        sub_category_l3: "",
        sub_category_l4: "",
        sub_category_l5: "",
        description: "",
        direct_url: "",
      },
    ]);
  };

  const removeExcelRow = (index) => {
    if (excelData.length > 1) {
      setExcelData(excelData.filter((_, i) => i !== index));
    }
  };

  const updateExcelCell = (rowIndex, field, value) => {
    const newData = [...excelData];
    newData[rowIndex][field] = value;
    setExcelData(newData);
  };

  const handleExcelSubmit = async () => {
    try {
      setExcelEditing(true);
      setError("");

      // Validate mandatory fields
      const validationErrors = [];
      excelData.forEach((row, index) => {
        if (!row.category.trim()) {
          validationErrors.push(`Row ${index + 1}: Category is required`);
        }
        if (!row.description.trim()) {
          validationErrors.push(`Row ${index + 1}: Description is required`);
        }
        if (!row.direct_url.trim()) {
          validationErrors.push(`Row ${index + 1}: URL is required`);
        }
      });

      if (validationErrors.length > 0) {
        setError(`Validation Errors:\n${validationErrors.join("\n")}`);
        return;
      }

      // Filter out empty rows (rows with only empty fields)
      const validData = excelData.filter(
        (row) =>
          row.category.trim() &&
          row.description.trim() &&
          row.direct_url.trim(),
      );

      if (validData.length === 0) {
        setError(
          "Please enter at least one service with category, description, and URL",
        );
        return;
      }

      // Convert to CSV format and upload
      const csvContent = convertToCSV(validData);
      const blob = new Blob([csvContent], { type: "text/csv" });
      const formData = new FormData();
      formData.append("file", blob, "excel_data.csv");

      const result = await servicesAPI.bulkUploadServices(formData);
      setUploadResult(result);

      if (result.success_count > 0) {
        alert(`✅ Successfully uploaded ${result.success_count} service(s)!`);
        loadCategories();
      }
      if (result.error_count > 0) {
        alert(
          `❌ Error uploading ${result.error_count} service(s). Please check the details.`,
        );
      }

      // Reset form
      setExcelData([
        {
          category: "",
          sub_category_l0: "",
          sub_category_l1: "",
          sub_category_l2: "",
          sub_category_l3: "",
          sub_category_l4: "",
          sub_category_l5: "",
          description: "",
          direct_url: "",
        },
      ]);
      setShowExcelEditor(false);
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setExcelEditing(false);
    }
  };

  const convertToCSV = (data) => {
    const headers = [
      "Category",
      "Sub Category L0",
      "Sub Category L1",
      "Sub Category L2",
      "Sub Category L3",
      "Sub Category L4",
      "Sub Category L5",
      "Description",
      "Direct URL",
    ];
    const csvRows = [headers.join(",")];

    data.forEach((row) => {
      const values = [
        `"${row.category || ""}"`,
        `"${row.sub_category_l0 || ""}"`,
        `"${row.sub_category_l1 || ""}"`,
        `"${row.sub_category_l2 || ""}"`,
        `"${row.sub_category_l3 || ""}"`,
        `"${row.sub_category_l4 || ""}"`,
        `"${row.sub_category_l5 || ""}"`,
        `"${row.description || ""}"`,
        `"${row.direct_url || ""}"`,
      ];
      csvRows.push(values.join(","));
    });

    return csvRows.join("\n");
  };

  const handlePasteData = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text");
    const rows = pasteData.split("\n").filter((row) => row.trim());

    if (rows.length > 0) {
      const newData = rows.map((row) => {
        const columns = row
          .split("\t")
          .map((col) => col.trim().replace(/^"|"$/g, ""));
        return {
          category: columns[0] || "",
          sub_category_l0: columns[1] || "",
          sub_category_l1: columns[2] || "",
          sub_category_l2: columns[3] || "",
          sub_category_l3: columns[4] || "",
          sub_category_l4: columns[5] || "",
          sub_category_l5: columns[6] || "",
          description: columns[7] || "",
          direct_url: columns[8] || "",
        };
      });
      setExcelData(newData);
    }
  };

  // Export functions

  const handleExportCSV = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await servicesAPI.downloadExport();

      // Create and download CSV file
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `services_export_${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const downloadCSVTemplate = () => {
    if (!templateData) return;

    const headers = templateData.headers.join(",");
    const sampleRows = templateData.sample_rows
      .map((row) =>
        templateData.headers
          .map((header) => `"${row[header] || ""}"`)
          .join(","),
      )
      .join("\n");

    const csvContent = `${headers}\n${sampleRows}`;
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = templateData.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Recycle bin functions
  const loadDeletedServices = async () => {
    try {
      setRecycleBinLoading(true);
      setError("");

      const deletedServices = await servicesAPI.getDeletedServices();
      setDeletedServices(deletedServices);
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setRecycleBinLoading(false);
    }
  };

  const restoreService = async (serviceId) => {
    try {
      setLoading(true);
      setError("");

      await servicesAPI.restoreService(serviceId);

      // Refresh the recycle bin and main data
      await loadDeletedServices();
      await refreshData();

      setError("");
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const permanentDeleteService = async (serviceId) => {
    if (
      !window.confirm(
        "Are you sure you want to permanently delete this service? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      setError("");

      await servicesAPI.permanentDeleteService(serviceId);

      // Refresh the recycle bin
      await loadDeletedServices();

      setError("");
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    try {
      setLoading(true);
      setError("");

      // Always reload categories
      await loadCategories();

      // If we're in a specific path, reload subcategories for that path
      if (currentPath.length > 0) {
        await loadSubcategories(currentPath);
      }
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleBackToRoot = () => {
    setCurrentPath([]);
    setSubcategories([]);
    setFinalServices([]);
    setAllServices([]);
    setNewSubcategory("");
    setSubcategoryIsLeafLevel(false);
    setSubcategoryDescription("");
    setSubcategoryDirectUrl("");
  };

  const handleCategoryClick = (category) => {
    const newPath = [category];
    setCurrentPath(newPath);
    loadSubcategories(newPath);
  };

  const handleSubcategoryClick = (subcategory) => {
    const newPath = [...currentPath, subcategory];
    setCurrentPath(newPath);
    loadSubcategories(newPath);
  };

  const handleBreadcrumbClick = (index) => {
    const newPath = currentPath.slice(0, index + 1);
    setCurrentPath(newPath);
    loadSubcategories(newPath);
  };

  const getCurrentLevel = () => {
    return currentPath.length;
  };

  const getLevelName = (level) => {
    const levelNames = ["Category", "L0", "L1", "L2", "L3", "L4", "L5"];
    return levelNames[level] || `Level ${level}`;
  };

  const getServiceLevel = (service) => {
    if (service.sub_category_l5) return 6;
    if (service.sub_category_l4) return 5;
    if (service.sub_category_l3) return 4;
    if (service.sub_category_l2) return 3;
    if (service.sub_category_l1) return 2;
    if (service.sub_category_l0) return 1;
    return 0;
  };

  const getServiceNameAtLevel = (service, level) => {
    switch (level) {
      case 0:
        return service.category;
      case 1:
        return service.sub_category_l0;
      case 2:
        return service.sub_category_l1;
      case 3:
        return service.sub_category_l2;
      case 4:
        return service.sub_category_l3;
      case 5:
        return service.sub_category_l4;
      case 6:
        return service.sub_category_l5;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">CRM Admin Panel</h1>

      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("services")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "services"
                ? "border-red-500 text-red-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Services Management
          </button>
          <button
            onClick={() => setActiveTab("faq")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "faq"
                ? "border-red-500 text-red-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            FAQ Management
          </button>
          <button
            onClick={() => setActiveTab("logs")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "logs"
                ? "border-red-500 text-red-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Logs
          </button>
          <button
            onClick={() => setActiveTab("tickets")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "tickets"
                ? "border-red-500 text-red-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Tickets
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "faq" ? (
        <FAQManagement />
      ) : activeTab === "logs" ? (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <h2 className="text-xl font-semibold mb-3">Conversation Logs</h2>
          <p className="text-sm text-gray-600 mb-4">
            User and assistant messages by session (security audit). Filter by date, session_id, or token.
          </p>
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="text-sm text-gray-600 self-center">Date:</span>
            {["all", "today", "week", "month", "custom"].map((preset) => (
              <button
                key={preset}
                onClick={() => setLogsDatePreset(preset)}
                className={`px-3 py-1.5 rounded text-sm font-medium ${
                  logsDatePreset === preset
                    ? "bg-red-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {preset === "all" ? "All" : preset === "today" ? "Today" : preset === "week" ? "This week" : preset === "month" ? "This month" : "Custom"}
              </button>
            ))}
            {logsDatePreset === "custom" && (
              <>
                <input
                  type="date"
                  value={logsFromDate}
                  onChange={(e) => setLogsFromDate(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 text-sm"
                />
                <span className="self-center text-gray-500">to</span>
                <input
                  type="date"
                  value={logsToDate}
                  onChange={(e) => setLogsToDate(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </>
            )}
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            <input
              type="text"
              placeholder="Filter by session_id"
              value={logsSessionFilter}
              onChange={(e) => setLogsSessionFilter(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 text-sm w-48"
            />
            <input
              type="text"
              placeholder="Filter by token"
              value={logsTokenFilter}
              onChange={(e) => setLogsTokenFilter(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 text-sm w-48"
            />
            <select
              value={logsLimit}
              onChange={(e) => setLogsLimit(Number(e.target.value))}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
            </select>
            <button
              onClick={loadLogs}
              disabled={logsLoading}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm font-medium disabled:opacity-50"
            >
              {logsLoading ? "Loading…" : "Refresh"}
            </button>
            <button
              onClick={exportLogsToExcel}
              disabled={logsExporting}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-medium disabled:opacity-50"
            >
              {logsExporting ? "Exporting…" : "Export to Excel (CSV)"}
            </button>
          </div>
          {logsError && (
            <div className="mb-3 p-2 bg-red-50 text-red-700 rounded text-sm">{logsError}</div>
          )}
          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto border border-gray-200 rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="text-left py-2 px-3">Timestamp</th>
                  <th className="text-left py-2 px-3">Session ID</th>
                  <th className="text-left py-2 px-3">Token</th>
                  <th className="text-left py-2 px-3">Role</th>
                  <th className="text-left py-2 px-3">Content</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && !logsLoading && (
                  <tr><td colSpan={5} className="py-4 px-3 text-gray-500">No logs found.</td></tr>
                )}
                {logs.map((log) => (
                  <tr key={log.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="py-2 px-3 text-gray-600 whitespace-nowrap" title={log.created_at || ""}>
                      {formatLogTimestamp(log.created_at)}
                    </td>
                    <td className="py-2 px-3 font-mono text-xs max-w-[120px] truncate" title={log.session_id}>{log.session_id}</td>
                    <td className="py-2 px-3 font-mono text-xs max-w-[100px] truncate" title={log.token || ""}>{log.token || "—"}</td>
                    <td className="py-2 px-3">
                      <span className={log.role === "user" ? "text-red-600 font-medium" : "text-gray-700"}>{log.role}</span>
                    </td>
                    <td className="py-2 px-3 max-w-md truncate" title={log.content}>{log.content}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-gray-500">Showing {logs.length} log entries.</p>
        </div>
      ) : activeTab === "tickets" ? (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <h2 className="text-xl font-semibold mb-3">Tickets</h2>
          <p className="text-sm text-gray-600 mb-4">
            All sessions with a pending or confirmed ticket (in-memory; resets on server restart). Export includes &quot;Exported at&quot; timestamp.
          </p>
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="text-sm text-gray-600 self-center">Date:</span>
            {["all", "today", "week", "month", "custom"].map((preset) => (
              <button
                key={preset}
                onClick={() => setTicketsDatePreset(preset)}
                disabled
                title="Date filter not available for in-memory tickets"
                className="px-3 py-1.5 rounded text-sm font-medium bg-gray-100 text-gray-400 cursor-not-allowed"
              >
                {preset === "all" ? "All" : preset === "today" ? "Today" : preset === "week" ? "This week" : preset === "month" ? "This month" : "Custom"}
              </button>
            ))}
            {ticketsDatePreset === "custom" && (
              <>
                <input type="date" value={ticketsFromDate} onChange={(e) => setTicketsFromDate(e.target.value)} disabled className="border border-gray-200 rounded px-3 py-2 text-sm bg-gray-50" />
                <span className="self-center text-gray-400">to</span>
                <input type="date" value={ticketsToDate} onChange={(e) => setTicketsToDate(e.target.value)} disabled className="border border-gray-200 rounded px-3 py-2 text-sm bg-gray-50" />
              </>
            )}
            <span className="text-xs text-gray-500 self-center ml-1">(Date filter for tickets when stored with timestamps)</span>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={loadTickets}
              disabled={ticketsLoading}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm font-medium disabled:opacity-50"
            >
              {ticketsLoading ? "Loading…" : "Refresh"}
            </button>
            <button
              onClick={exportTicketsToExcel}
              disabled={ticketsExporting}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-medium disabled:opacity-50"
            >
              {ticketsExporting ? "Exporting…" : "Export to Excel (CSV)"}
            </button>
          </div>
          {ticketsError && (
            <div className="mb-3 p-2 bg-red-50 text-red-700 rounded text-sm">{ticketsError}</div>
          )}
          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto border border-gray-200 rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="text-left py-2 px-3">Timestamp</th>
                  <th className="text-left py-2 px-3">Session ID</th>
                  <th className="text-left py-2 px-3">Domain</th>
                  <th className="text-left py-2 px-3">Sub Cat Head</th>
                  <th className="text-left py-2 px-3">Sub Category</th>
                  <th className="text-left py-2 px-3">Query</th>
                  <th className="text-left py-2 px-3">Token</th>
                </tr>
              </thead>
              <tbody>
                {tickets.length === 0 && !ticketsLoading && (
                  <tr><td colSpan={7} className="py-4 px-3 text-gray-500">No tickets.</td></tr>
                )}
                {tickets.map((t, i) => (
                  <tr key={t.session_id + String(i)} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="py-2 px-3 text-gray-400">—</td>
                    <td className="py-2 px-3 font-mono text-xs max-w-[140px] truncate" title={t.session_id}>{t.session_id}</td>
                    <td className="py-2 px-3">{t.domain || "—"}</td>
                    <td className="py-2 px-3">{t.sub_cat_head || "—"}</td>
                    <td className="py-2 px-3">{t.sub_category || "—"}</td>
                    <td className="py-2 px-3 max-w-[200px] truncate" title={t.query}>{t.query || "—"}</td>
                    <td className="py-2 px-3 font-mono text-xs max-w-[100px] truncate" title={t.token}>{t.token || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-gray-500">Showing {tickets.length} ticket(s). CSV export includes &quot;Exported at&quot; timestamp.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Categories Section */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="mb-4">
              <h2 className="text-xl font-semibold mb-3">Categories</h2>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={refreshData}
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-md transition-colors flex items-center gap-2 text-sm font-medium"
                  title="Reloads data"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Refresh
                </button>
                <button
                  onClick={handleBackToRoot}
                  disabled={loading}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-md transition-colors flex items-center gap-2 text-sm font-medium"
                  title="Returns to root level"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                  Reset
                </button>
                <button
                  type="button"
                  onClick={() => setShowBulkUpload(!showBulkUpload)}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-md transition-colors flex items-center gap-2 text-sm font-medium"
                  title="Opens bulk upload form"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  Bulk Upload
                </button>
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md transition-colors flex items-center gap-2 text-sm font-medium"
                  title="Downloads CSV template"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Download Template
                </button>
                <button
                  type="button"
                  onClick={handleExportCSV}
                  disabled={loading}
                  className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 rounded-md transition-colors flex items-center gap-2 text-sm font-medium"
                  title="Exports all data as CSV"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Export CSV
                </button>
                <button
                  type="button"
                  onClick={() => setShowExcelEditor(!showExcelEditor)}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-2 rounded-md transition-colors flex items-center gap-2 text-sm font-medium"
                  title="Excel-style inline editor for bulk data entry"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                  </svg>
                  Excel Editor
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowRecycleBin(!showRecycleBin);
                    if (!showRecycleBin) {
                      loadDeletedServices();
                    }
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md transition-colors flex items-center gap-2 text-sm font-medium"
                  title="View and restore deleted services"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  Recycle Bin
                </button>
              </div>
            </div>

            {/* Create Category Form */}
            <form onSubmit={handleCreateCategory} className="mb-4">
              <div className="space-y-3">
                {/* Category Name Input */}
                <div className="flex flex-wrap gap-2">
                  <input
                    type="text"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder="New category name"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    disabled={loading}
                  />
                  <button
                    type="submit"
                    disabled={loading || !newCategory.trim()}
                    className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>

                {/* Creation Type Toggle */}
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={!categoryIsLeafLevel}
                      onChange={() => setCategoryIsLeafLevel(false)}
                      className="text-red-600"
                      disabled={loading}
                    />
                    <span className="text-sm">
                      Intermediate Level (for future subcategories)
                    </span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={categoryIsLeafLevel}
                      onChange={() => setCategoryIsLeafLevel(true)}
                      className="text-red-600"
                      disabled={loading}
                    />
                    <span className="text-sm">
                      Leaf Level (final service with description/URL)
                    </span>
                  </label>
                </div>

                {/* Description and URL Fields - Only for Leaf Level */}
                {categoryIsLeafLevel && (
                  <div className="space-y-2 p-3 bg-red-50 rounded-md">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description *
                      </label>
                      <textarea
                        value={categoryDescription}
                        onChange={(e) => setCategoryDescription(e.target.value)}
                        placeholder="Enter service description"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                        rows="2"
                        disabled={loading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Direct URL *
                      </label>
                      <input
                        type="url"
                        value={categoryDirectUrl}
                        onChange={(e) => setCategoryDirectUrl(e.target.value)}
                        placeholder="https://example.com/service"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                        disabled={loading}
                      />
                    </div>
                  </div>
                )}
              </div>
            </form>

            {/* Bulk Upload Form */}
            {showBulkUpload && (
              <div className="mb-4 p-4 bg-purple-50 rounded-md border border-purple-200">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-medium text-purple-800">
                    Bulk Upload Services
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowBulkUpload(false)}
                    className="text-purple-600 hover:text-purple-800"
                    disabled={uploading}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <form onSubmit={handleBulkUpload} className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select CSV File
                    </label>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      disabled={uploading}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      File must be in CSV format with headers: Category, Sub
                      Category L0, Sub Category L1, etc.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={uploading || !uploadFile}
                      className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 disabled:opacity-50 flex items-center gap-2"
                    >
                      {uploading ? (
                        <>
                          <svg
                            className="animate-spin h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                            />
                          </svg>
                          Upload Services
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowBulkUpload(false)}
                      className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                      disabled={uploading}
                    >
                      Cancel
                    </button>
                  </div>
                </form>

                {/* Upload Result */}
                {uploadResult && (
                  <div className="mt-3 p-3 bg-white rounded-md border">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-gray-800">
                        Upload Results:
                      </h4>
                      <button
                        type="button"
                        onClick={() => setUploadResult(null)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                    <div className="text-sm space-y-1">
                      <p className="text-green-600">
                        ✅ Successfully uploaded: {uploadResult.success_count}{" "}
                        services
                      </p>
                      {uploadResult.error_count > 0 && (
                        <p className="text-red-600">
                          ❌ Errors: {uploadResult.error_count} services
                        </p>
                      )}
                      {uploadResult.errors.length > 0 && (
                        <div className="mt-2">
                          <p className="font-medium text-red-600">
                            Error Details:
                          </p>
                          <ul className="list-disc list-inside text-red-600 text-xs space-y-1">
                            {uploadResult.errors.map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Excel-style Editor */}
            {showExcelEditor && (
              <div
                className={`mb-4 p-4 bg-yellow-50 rounded-md border border-yellow-200 ${excelEditorMinimized ? "minimized" : ""}`}
              >
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-medium text-yellow-800">
                    Excel-style Service Editor
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setExcelEditorMinimized(!excelEditorMinimized)
                      }
                      className="text-yellow-600 hover:text-yellow-800"
                      disabled={excelEditing}
                      title={excelEditorMinimized ? "Maximize" : "Minimize"}
                    >
                      {excelEditorMinimized ? (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M20 12H4"
                          />
                        </svg>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowExcelEditor(false)}
                      className="text-yellow-600 hover:text-yellow-800"
                      disabled={excelEditing}
                      title="Close"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                {excelEditorMinimized ? (
                  <div className="text-center py-4">
                    <p className="text-yellow-700 text-sm">
                      Excel Editor is minimized. Click the maximize button to
                      expand.
                    </p>
                    <div className="mt-2 flex justify-center gap-2">
                      <button
                        type="button"
                        onClick={addExcelRow}
                        className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600 flex items-center gap-1"
                        disabled={excelEditing}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                        Add Row
                      </button>
                      <button
                        type="button"
                        onClick={handleExcelSubmit}
                        disabled={excelEditing}
                        className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600 flex items-center gap-1"
                      >
                        {excelEditing ? (
                          <>
                            <svg
                              className="animate-spin h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Saving...
                          </>
                        ) : (
                          <>
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                              />
                            </svg>
                            Save All
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-3">
                      {/* Controls */}
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={addExcelRow}
                          className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600 flex items-center gap-1"
                          disabled={excelEditing}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                            />
                          </svg>
                          Add Row
                        </button>
                        <button
                          type="button"
                          onClick={handleExcelSubmit}
                          disabled={excelEditing}
                          className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600 flex items-center gap-1"
                        >
                          {excelEditing ? (
                            <>
                              <svg
                                className="animate-spin h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              Saving...
                            </>
                          ) : (
                            <>
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                                />
                              </svg>
                              Save All
                            </>
                          )}
                        </button>
                      </div>

                      {/* Paste Instructions */}
                      <div className="bg-red-50 p-3 rounded-md">
                        <p className="text-sm text-red-800">
                          <strong>💡 Tip:</strong> You can paste data directly
                          from Excel! Copy cells from Excel and paste (Ctrl+V)
                          anywhere in the table below. Make sure your Excel
                          columns match: Category | L0 | L1 | L2 | L3 | L4 | L5
                          | Description | URL
                        </p>
                      </div>

                      {/* Excel-style Table */}
                      <div
                        className="overflow-x-auto"
                        onPaste={handlePasteData}
                      >
                        <table className="min-w-full bg-white border border-gray-200 rounded-md">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200">
                                #
                              </th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200 bg-red-50">
                                Category *
                              </th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200">
                                L0
                              </th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200">
                                L1
                              </th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200">
                                L2
                              </th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200">
                                L3
                              </th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200">
                                L4
                              </th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200">
                                L5
                              </th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200 bg-red-50">
                                Description *
                              </th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200 bg-red-50">
                                URL *
                              </th>
                              <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {excelData.map((row, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-2 py-2 text-xs text-gray-500 border-r border-gray-200">
                                  {index + 1}
                                </td>
                                <td className="px-1 py-1 border-r border-gray-200">
                                  <input
                                    type="text"
                                    value={row.category}
                                    onChange={(e) =>
                                      updateExcelCell(
                                        index,
                                        "category",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Category"
                                    className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                                    disabled={excelEditing}
                                  />
                                </td>
                                <td className="px-1 py-1 border-r border-gray-200">
                                  <input
                                    type="text"
                                    value={row.sub_category_l0}
                                    onChange={(e) =>
                                      updateExcelCell(
                                        index,
                                        "sub_category_l0",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="L0"
                                    className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                                    disabled={excelEditing}
                                  />
                                </td>
                                <td className="px-1 py-1 border-r border-gray-200">
                                  <input
                                    type="text"
                                    value={row.sub_category_l1}
                                    onChange={(e) =>
                                      updateExcelCell(
                                        index,
                                        "sub_category_l1",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="L1"
                                    className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                                    disabled={excelEditing}
                                  />
                                </td>
                                <td className="px-1 py-1 border-r border-gray-200">
                                  <input
                                    type="text"
                                    value={row.sub_category_l2}
                                    onChange={(e) =>
                                      updateExcelCell(
                                        index,
                                        "sub_category_l2",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="L2"
                                    className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                                    disabled={excelEditing}
                                  />
                                </td>
                                <td className="px-1 py-1 border-r border-gray-200">
                                  <input
                                    type="text"
                                    value={row.sub_category_l3}
                                    onChange={(e) =>
                                      updateExcelCell(
                                        index,
                                        "sub_category_l3",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="L3"
                                    className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                                    disabled={excelEditing}
                                  />
                                </td>
                                <td className="px-1 py-1 border-r border-gray-200">
                                  <input
                                    type="text"
                                    value={row.sub_category_l4}
                                    onChange={(e) =>
                                      updateExcelCell(
                                        index,
                                        "sub_category_l4",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="L4"
                                    className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                                    disabled={excelEditing}
                                  />
                                </td>
                                <td className="px-1 py-1 border-r border-gray-200">
                                  <input
                                    type="text"
                                    value={row.sub_category_l5}
                                    onChange={(e) =>
                                      updateExcelCell(
                                        index,
                                        "sub_category_l5",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="L5"
                                    className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                                    disabled={excelEditing}
                                  />
                                </td>
                                <td className="px-1 py-1 border-r border-gray-200">
                                  <input
                                    type="text"
                                    value={row.description}
                                    onChange={(e) =>
                                      updateExcelCell(
                                        index,
                                        "description",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Description"
                                    className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                                    disabled={excelEditing}
                                  />
                                </td>
                                <td className="px-1 py-1 border-r border-gray-200">
                                  <input
                                    type="url"
                                    value={row.direct_url}
                                    onChange={(e) =>
                                      updateExcelCell(
                                        index,
                                        "direct_url",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="https://..."
                                    className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                                    disabled={excelEditing}
                                  />
                                </td>
                                <td className="px-1 py-1 text-center">
                                  <button
                                    type="button"
                                    onClick={() => removeExcelRow(index)}
                                    disabled={
                                      excelData.length === 1 || excelEditing
                                    }
                                    className="text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Remove this row"
                                  >
                                    <svg
                                      className="w-4 h-4"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                      />
                                    </svg>
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <p className="text-xs text-gray-600">
                        * Required fields. Add multiple rows and save all at
                        once. Empty rows will be ignored.
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Full-Screen Excel Editor Modal */}
            {showExcelEditor && !excelEditorMinimized && (
              <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-7xl max-h-screen overflow-hidden">
                  {/* Modal Header */}
                  <div className="flex justify-between items-center p-4 border-b bg-yellow-50">
                    <h3 className="text-xl font-semibold text-yellow-800">
                      Excel-style Service Editor - Full Screen
                    </h3>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setExcelEditorMinimized(true)}
                        className="text-yellow-600 hover:text-yellow-800"
                        disabled={excelEditing}
                        title="Minimize to inline view"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M20 12H4"
                          />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowExcelEditor(false)}
                        className="text-yellow-600 hover:text-yellow-800"
                        disabled={excelEditing}
                        title="Close"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Modal Content */}
                  <div className="p-6 h-full overflow-auto">
                    <div className="space-y-4">
                      {/* Paste Instructions */}
                      <div className="bg-red-50 p-4 rounded-md">
                        <p className="text-sm text-red-800">
                          <strong>💡 Tip:</strong> You can paste data directly
                          from Excel! Copy cells from Excel and paste (Ctrl+V)
                          anywhere in the table below. Make sure your Excel
                          columns match: Category | L0 | L1 | L2 | L3 | L4 | L5
                          | Description | URL
                        </p>
                      </div>

                      {/* Controls */}
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={addExcelRow}
                          className="px-4 py-2 bg-green-500 text-white rounded text-sm hover:bg-green-600 flex items-center gap-2"
                          disabled={excelEditing}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                            />
                          </svg>
                          Add Row
                        </button>
                        <button
                          type="button"
                          onClick={handleExcelSubmit}
                          disabled={excelEditing}
                          className="px-4 py-2 bg-red-500 text-white rounded text-sm hover:bg-red-600 flex items-center gap-2"
                        >
                          {excelEditing ? (
                            <>
                              <svg
                                className="animate-spin h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              Saving...
                            </>
                          ) : (
                            <>
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                                />
                              </svg>
                              Save All
                            </>
                          )}
                        </button>
                      </div>

                      {/* Excel-style Table */}
                      <div
                        className="overflow-x-auto border border-gray-200 rounded-md"
                        onPaste={handlePasteData}
                      >
                        <table className="min-w-full bg-white">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase border-r border-gray-200">
                                #
                              </th>
                              <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase border-r border-gray-200 bg-red-50">
                                Category *
                              </th>
                              <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase border-r border-gray-200">
                                L0
                              </th>
                              <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase border-r border-gray-200">
                                L1
                              </th>
                              <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase border-r border-gray-200">
                                L2
                              </th>
                              <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase border-r border-gray-200">
                                L3
                              </th>
                              <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase border-r border-gray-200">
                                L4
                              </th>
                              <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase border-r border-gray-200">
                                L5
                              </th>
                              <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase border-r border-gray-200 bg-red-50">
                                Description *
                              </th>
                              <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase border-r border-gray-200 bg-red-50">
                                URL *
                              </th>
                              <th className="px-3 py-3 text-center text-sm font-medium text-gray-500 uppercase">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {excelData.map((row, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-3 py-3 text-sm text-gray-500 border-r border-gray-200">
                                  {index + 1}
                                </td>
                                <td className="px-2 py-2 border-r border-gray-200">
                                  <input
                                    type="text"
                                    value={row.category}
                                    onChange={(e) =>
                                      updateExcelCell(
                                        index,
                                        "category",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Category"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                                    disabled={excelEditing}
                                  />
                                </td>
                                <td className="px-2 py-2 border-r border-gray-200">
                                  <input
                                    type="text"
                                    value={row.sub_category_l0}
                                    onChange={(e) =>
                                      updateExcelCell(
                                        index,
                                        "sub_category_l0",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="L0"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                                    disabled={excelEditing}
                                  />
                                </td>
                                <td className="px-2 py-2 border-r border-gray-200">
                                  <input
                                    type="text"
                                    value={row.sub_category_l1}
                                    onChange={(e) =>
                                      updateExcelCell(
                                        index,
                                        "sub_category_l1",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="L1"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                                    disabled={excelEditing}
                                  />
                                </td>
                                <td className="px-2 py-2 border-r border-gray-200">
                                  <input
                                    type="text"
                                    value={row.sub_category_l2}
                                    onChange={(e) =>
                                      updateExcelCell(
                                        index,
                                        "sub_category_l2",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="L2"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                                    disabled={excelEditing}
                                  />
                                </td>
                                <td className="px-2 py-2 border-r border-gray-200">
                                  <input
                                    type="text"
                                    value={row.sub_category_l3}
                                    onChange={(e) =>
                                      updateExcelCell(
                                        index,
                                        "sub_category_l3",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="L3"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                                    disabled={excelEditing}
                                  />
                                </td>
                                <td className="px-2 py-2 border-r border-gray-200">
                                  <input
                                    type="text"
                                    value={row.sub_category_l4}
                                    onChange={(e) =>
                                      updateExcelCell(
                                        index,
                                        "sub_category_l4",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="L4"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                                    disabled={excelEditing}
                                  />
                                </td>
                                <td className="px-2 py-2 border-r border-gray-200">
                                  <input
                                    type="text"
                                    value={row.sub_category_l5}
                                    onChange={(e) =>
                                      updateExcelCell(
                                        index,
                                        "sub_category_l5",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="L5"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                                    disabled={excelEditing}
                                  />
                                </td>
                                <td className="px-2 py-2 border-r border-gray-200">
                                  <input
                                    type="text"
                                    value={row.description}
                                    onChange={(e) =>
                                      updateExcelCell(
                                        index,
                                        "description",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Description"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                                    disabled={excelEditing}
                                  />
                                </td>
                                <td className="px-2 py-2 border-r border-gray-200">
                                  <input
                                    type="url"
                                    value={row.direct_url}
                                    onChange={(e) =>
                                      updateExcelCell(
                                        index,
                                        "direct_url",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="https://..."
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                                    disabled={excelEditing}
                                  />
                                </td>
                                <td className="px-2 py-2 text-center">
                                  <button
                                    type="button"
                                    onClick={() => removeExcelRow(index)}
                                    disabled={
                                      excelData.length === 1 || excelEditing
                                    }
                                    className="text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Remove this row"
                                  >
                                    <svg
                                      className="w-5 h-5"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                      />
                                    </svg>
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <p className="text-sm text-gray-600">
                        * Required fields. Add multiple rows and save all at
                        once. Empty rows will be ignored.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Recycle Bin Modal */}
            {showRecycleBin && (
              <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                <div
                  className={`bg-white rounded-lg shadow-xl w-full max-h-screen overflow-hidden ${
                    recycleBinMinimized ? "max-w-md" : "max-w-6xl"
                  }`}
                >
                  {/* Modal Header */}
                  <div className="flex justify-between items-center p-4 border-b bg-red-50">
                    <h3 className="text-xl font-semibold text-red-800">
                      {recycleBinMinimized
                        ? "🗑️ Recycle Bin"
                        : "Recycle Bin - Deleted Services"}
                    </h3>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setRecycleBinMinimized(!recycleBinMinimized)
                        }
                        className="text-red-600 hover:text-red-800"
                        disabled={recycleBinLoading}
                        title={recycleBinMinimized ? "Maximize" : "Minimize"}
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          {recycleBinMinimized ? (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                            />
                          ) : (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 9V4.5M9 9H4.5M9 9L3.5 3.5M15 9v-4.5M15 9h4.5M15 9l5.5-5.5M9 15v4.5M9 15H4.5M9 15l-5.5 5.5M15 15v4.5M15 15h4.5m-4.5 0l5.5 5.5"
                            />
                          )}
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowRecycleBin(false)}
                        className="text-red-600 hover:text-red-800"
                        disabled={recycleBinLoading}
                        title="Close"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Modal Content */}
                  <div className="p-6 h-full overflow-auto">
                    {recycleBinMinimized ? (
                      <div className="text-center py-4">
                        <div className="flex items-center justify-center gap-2 mb-3">
                          <svg
                            className="w-8 h-8 text-red-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                          <span className="text-red-700 font-medium">
                            {deletedServices.length} deleted items
                          </span>
                        </div>
                        <p className="text-red-600 text-sm">
                          Click maximize to view and manage deleted services
                        </p>
                      </div>
                    ) : recycleBinLoading ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
                        <p className="mt-2 text-gray-600">
                          Loading deleted services...
                        </p>
                      </div>
                    ) : deletedServices.length === 0 ? (
                      <div className="text-center py-8">
                        <svg
                          className="w-16 h-16 text-gray-400 mx-auto mb-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          Recycle Bin is Empty
                        </h3>
                        <p className="text-gray-600">
                          No deleted services found.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="bg-red-50 p-4 rounded-md">
                          <p className="text-sm text-red-800">
                            <strong>💡 Info:</strong> These services were moved
                            to the recycle bin. You can restore them or
                            permanently delete them.
                          </p>
                        </div>

                        {/* Bulk Operations Toolbar */}
                        {deletedServices.length > 0 && (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md border">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={
                                  deletedServices.length > 0 &&
                                  selectedDeletedServices.length === deletedServices.length
                                }
                                onChange={handleSelectAllDeletedServices}
                                className="mr-2 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                              />
                              <span className="text-sm font-medium text-gray-700">
                                Select All ({deletedServices.length})
                              </span>
                            </label>
                            {selectedDeletedServices.length > 0 && (
                              <div className="flex items-center gap-3">
                                <span className="text-sm text-red-600">
                                  {selectedDeletedServices.length} selected
                                </span>
                                <button
                                  onClick={() => openBulkDeleteConfirm("restore")}
                                  className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-sm flex items-center gap-1"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                                    />
                                  </svg>
                                  Restore Selected
                                </button>
                                <button
                                  onClick={() => openBulkDeleteConfirm("permanent")}
                                  className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 text-sm flex items-center gap-1"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                  </svg>
                                  Delete Permanently
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="grid gap-4">
                          {deletedServices.map((service) => {
                            const hierarchyPath = [
                              service.category,
                              service.sub_category_l0,
                              service.sub_category_l1,
                              service.sub_category_l2,
                              service.sub_category_l3,
                              service.sub_category_l4,
                              service.sub_category_l5,
                            ].filter(Boolean);

                            return (
                              <div
                                key={service.id}
                                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50"
                              >
                                <div className="flex justify-between items-start">
                                  <div className="flex items-start gap-3 flex-1">
                                    <input
                                      type="checkbox"
                                      checked={selectedDeletedServices.includes(service.id)}
                                      onChange={() => handleSelectDeletedService(service.id)}
                                      className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                                    />
                                    <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                      <svg
                                        className="w-5 h-5 text-red-500"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                        />
                                      </svg>
                                      <h4 className="font-medium text-gray-900">
                                        {hierarchyPath.join(" → ")}
                                      </h4>
                                    </div>

                                    {service.description && (
                                      <p className="text-sm text-gray-600 mb-2">
                                        {service.description}
                                      </p>
                                    )}

                                    {service.direct_url && (
                                      <p className="text-sm text-red-600 mb-2">
                                        <a
                                          href={service.direct_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="hover:underline"
                                        >
                                          {service.direct_url}
                                        </a>
                                      </p>
                                    )}

                                    <div className="text-xs text-gray-500">
                                      <span className="font-medium">
                                        Deleted:
                                      </span>{" "}
                                      {new Date(
                                        service.deleted_at,
                                      ).toLocaleString()}
                                    </div>
                                    </div>
                                  </div>

                                  <div className="flex gap-2 ml-4">
                                    <button
                                      onClick={() => restoreService(service.id)}
                                      disabled={loading}
                                      className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 flex items-center gap-1 disabled:opacity-50"
                                      title="Restore this service"
                                    >
                                      <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                        />
                                      </svg>
                                      Restore
                                    </button>
                                    <button
                                      onClick={() =>
                                        permanentDeleteService(service.id)
                                      }
                                      disabled={loading}
                                      className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 flex items-center gap-1 disabled:opacity-50"
                                      title="Permanently delete this service"
                                    >
                                      <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                        />
                                      </svg>
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Categories List */}
            <div className="space-y-2">
              {/* Select All Categories */}
              {categories.length > 0 && (
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded-md border">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={
                        categories.length > 0 &&
                        selectedCategories.length === categories.length
                      }
                      onChange={handleSelectAllCategories}
                      className="mr-2 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Select All Categories ({categories.length})
                    </span>
                  </label>
                  {selectedCategories.length > 0 && (
                    <span className="text-sm text-red-600">
                      {selectedCategories.length} selected
                    </span>
                  )}
                </div>
              )}
              {categories.map((category, index) => (
                <div
                  key={index}
                  className="flex justify-between items-center p-3 bg-white rounded-md border hover:shadow-sm"
                >
                  {editingCategory === category ? (
                    <div className="flex-1 flex gap-2">
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(category)}
                        onChange={() => handleSelectCategory(category)}
                        className="mr-3 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                        disabled={true}
                      />
                      <input
                        type="text"
                        value={editCategoryName}
                        onChange={(e) => setEditCategoryName(e.target.value)}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                        disabled={loading}
                      />
                      <button
                        onClick={handleUpdateCategory}
                        className="px-2 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600 disabled:opacity-50"
                        disabled={loading}
                      >
                        Update
                      </button>
                      <button
                        onClick={() => {
                          setEditingCategory(null);
                          setEditCategoryName("");
                        }}
                        className="px-2 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                        disabled={loading}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center flex-1">
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(category)}
                          onChange={() => handleSelectCategory(category)}
                          className="mr-3 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                          disabled={loading}
                        />
                        <button
                          onClick={() => handleCategoryClick(category)}
                          className="flex-1 text-left hover:text-red-600 font-medium"
                        >
                          {category}
                        </button>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleEditCategory(category)}
                          className="px-2 py-1 text-red-600 hover:bg-red-100 rounded text-sm flex items-center gap-1"
                          disabled={loading}
                          title="Edit category"
                        >
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(category)}
                          className="px-2 py-1 text-red-600 hover:bg-red-100 rounded text-sm flex items-center gap-1"
                          disabled={loading}
                          title="Delete category"
                        >
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Subcategories Section */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h2 className="text-xl font-semibold mb-4">Hierarchy Navigation</h2>

            {/* Breadcrumb */}
            <div className="mb-4 flex items-center text-sm text-gray-600">
              <button
                onClick={handleBackToRoot}
                className="hover:text-red-600 font-medium"
              >
                Root
              </button>
              {currentPath.map((item, index) => (
                <React.Fragment key={index}>
                  <span className="mx-2">→</span>
                  <button
                    onClick={() => handleBreadcrumbClick(index)}
                    className="hover:text-red-600 font-medium"
                  >
                    {item}
                  </button>
                </React.Fragment>
              ))}
            </div>

            {/* Current Level Info */}
            {currentPath.length > 0 && (
              <div className="mb-4 p-3 bg-red-50 rounded-md">
                <p className="text-sm text-red-800">
                  <strong>Current Level:</strong>{" "}
                  {getLevelName(getCurrentLevel())}
                  {currentPath.length > 0 && ` (${currentPath.join(" → ")})`}
                </p>
              </div>
            )}

            {/* Create Subcategory Form */}
            {currentPath.length > 0 && currentPath.length < 4 && (
              <form onSubmit={handleCreateSubcategory} className="mb-4">
                <div className="space-y-3">
                  {/* Subcategory Name Input */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newSubcategory}
                      onChange={(e) => setNewSubcategory(e.target.value)}
                      placeholder={`New ${getLevelName(getCurrentLevel())} subcategory name`}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                      disabled={loading}
                    />
                    <button
                      type="submit"
                      disabled={loading || !newSubcategory.trim()}
                      className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50"
                    >
                      Add
                    </button>
                  </div>

                  {/* Creation Type Toggle */}
                  <div className="flex items-center gap-4">
                    <label
                      className={`flex items-center gap-2 ${isCurrentLevelLeaf ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <input
                        type="radio"
                        checked={!subcategoryIsLeafLevel}
                        onChange={() => setSubcategoryIsLeafLevel(false)}
                        className="text-green-600"
                        disabled={loading || isCurrentLevelLeaf}
                      />
                      <span className="text-sm">
                        Intermediate Level (for future subcategories)
                        {isCurrentLevelLeaf && (
                          <span className="text-red-500 ml-1">
                            (Disabled at leaf level)
                          </span>
                        )}
                      </span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={subcategoryIsLeafLevel}
                        onChange={() => setSubcategoryIsLeafLevel(true)}
                        className="text-green-600"
                        disabled={loading}
                      />
                      <span className="text-sm">
                        Leaf Level (final service with description/URL)
                      </span>
                    </label>
                  </div>

                  {/* Leaf Level Fields */}
                  {subcategoryIsLeafLevel && (
                    <div className="space-y-2 p-3 bg-green-50 rounded-md">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description *
                        </label>
                        <textarea
                          value={subcategoryDescription}
                          onChange={(e) =>
                            setSubcategoryDescription(e.target.value)
                          }
                          placeholder="Enter service description"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                          rows="2"
                          disabled={loading}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Direct URL *
                        </label>
                        <input
                          type="url"
                          value={subcategoryDirectUrl}
                          onChange={(e) =>
                            setSubcategoryDirectUrl(e.target.value)
                          }
                          placeholder="https://example.com/service"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                          disabled={loading}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </form>
            )}

            {/* Subcategories List */}
            <div className="space-y-2 mb-4">
              <h3 className="text-lg font-medium text-gray-700">
                Subcategories
              </h3>
              {/* Select All Subcategories */}
              {subcategories.length > 0 && (
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded-md border">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={
                        subcategories.length > 0 &&
                        subcategories.every((sub) =>
                          selectedSubcategories.some(
                            (s) =>
                              s.subcategory_name === sub &&
                              JSON.stringify(s.current_path) === JSON.stringify(currentPath)
                          )
                        )
                      }
                      onChange={handleSelectAllSubcategories}
                      className="mr-2 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Select All Subcategories ({subcategories.length})
                    </span>
                  </label>
                  {selectedSubcategories.filter(
                    (s) => JSON.stringify(s.current_path) === JSON.stringify(currentPath)
                  ).length > 0 && (
                    <span className="text-sm text-red-600">
                      {
                        selectedSubcategories.filter(
                          (s) => JSON.stringify(s.current_path) === JSON.stringify(currentPath)
                        ).length
                      }{" "}
                      selected
                    </span>
                  )}
                </div>
              )}
              {subcategories.map((subcategory, index) => (
                <div
                  key={index}
                  className="flex justify-between items-center p-3 bg-white rounded-md border hover:shadow-sm"
                >
                  {editingSubcategory === subcategory ? (
                    <div className="flex-1 flex gap-2">
                      <input
                        type="checkbox"
                        checked={selectedSubcategories.some(
                          (s) =>
                            s.subcategory_name === subcategory &&
                            JSON.stringify(s.current_path) === JSON.stringify(currentPath)
                        )}
                        onChange={() => handleSelectSubcategory(subcategory)}
                        className="mr-3 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                        disabled={true}
                      />
                      <input
                        type="text"
                        value={editSubcategoryName}
                        onChange={(e) => setEditSubcategoryName(e.target.value)}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                        disabled={loading}
                      />
                      <button
                        onClick={handleUpdateSubcategory}
                        className="px-2 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600 disabled:opacity-50"
                        disabled={loading}
                      >
                        Update
                      </button>
                      <button
                        onClick={() => {
                          setEditingSubcategory(null);
                          setEditSubcategoryName("");
                        }}
                        className="px-2 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                        disabled={loading}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center flex-1">
                        <input
                          type="checkbox"
                          checked={selectedSubcategories.some(
                            (s) =>
                              s.subcategory_name === subcategory &&
                              JSON.stringify(s.current_path) === JSON.stringify(currentPath)
                          )}
                          onChange={() => handleSelectSubcategory(subcategory)}
                          className="mr-3 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                          disabled={loading}
                        />
                        <button
                          onClick={() => handleSubcategoryClick(subcategory)}
                          className="flex-1 text-left hover:text-red-600 font-medium"
                        >
                          {subcategory}
                        </button>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleEditSubcategory(subcategory)}
                          className="px-2 py-1 text-red-600 hover:bg-red-100 rounded text-sm flex items-center gap-1"
                          disabled={loading}
                          title="Edit subcategory"
                        >
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteSubcategory(subcategory)}
                          className="px-2 py-1 text-red-600 hover:bg-red-100 rounded text-sm flex items-center gap-1"
                          disabled={loading}
                          title="Delete subcategory"
                        >
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
              {subcategories.length === 0 && currentPath.length > 0 && (
                <p className="text-gray-500 text-center py-4">
                  No subcategories at this level
                </p>
              )}
              {currentPath.length === 0 && (
                <p className="text-gray-500 text-center py-4">
                  Select a category to view subcategories
                </p>
              )}
            </div>

            {/* Final Services List */}
            {finalServices.length > 0 && (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-700">
                    Final Services
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEditService(finalServices[0])}
                      className="px-2 py-1 text-red-600 hover:bg-red-100 rounded text-sm flex items-center gap-1"
                      disabled={loading}
                      title="Edit service"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteService(finalServices[0].id)}
                      className="px-2 py-1 text-red-600 hover:bg-red-100 rounded text-sm flex items-center gap-1"
                      disabled={loading}
                      title="Delete service"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      Delete
                    </button>
                  </div>
                </div>
                {finalServices.map((service, index) => (
                  <div
                    key={index}
                    className="p-3 bg-green-50 border border-green-200 rounded-md"
                  >
                    {editingService && editingService.id === service.id ? (
                      // Edit mode
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description *
                          </label>
                          <textarea
                            value={editServiceData.description}
                            onChange={(e) =>
                              setEditServiceData({
                                ...editServiceData,
                                description: e.target.value,
                              })
                            }
                            placeholder="Enter service description"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            rows="2"
                            disabled={loading}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Direct URL *
                          </label>
                          <input
                            type="url"
                            value={editServiceData.direct_url}
                            onChange={(e) =>
                              setEditServiceData({
                                ...editServiceData,
                                direct_url: e.target.value,
                              })
                            }
                            placeholder="https://example.com/service"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            disabled={loading}
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={handleUpdateService}
                            className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
                            disabled={loading}
                          >
                            Update
                          </button>
                          <button
                            onClick={handleCancelEditService}
                            className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                            disabled={loading}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View mode
                      <div>
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <p className="font-medium text-green-800">
                              {service.description || "No description"}
                            </p>
                            {service.direct_url && (
                              <a
                                href={service.direct_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-green-600 hover:text-green-800 underline"
                              >
                                {service.direct_url}
                              </a>
                            )}
                          </div>
                          <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                            Service #{service.id}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* All Services at Current Level */}
            {allServices.length > 0 && (
              <div className="space-y-2 mt-4">
                <h3 className="text-lg font-medium text-gray-700">
                  All Services at Current Level
                </h3>
                <div className="max-h-60 overflow-y-auto">
                  {allServices.map((service, index) => (
                    <div
                      key={index}
                      className="p-2 bg-gray-50 border border-gray-200 rounded-md text-xs"
                    >
                      {editingService && editingService.id === service.id ? (
                        // Edit mode
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <strong>ID:</strong> {service.id}
                            </div>
                            <div>
                              <strong>Category:</strong> {service.category}
                            </div>
                            {service.sub_category_l0 && (
                              <div>
                                <strong>L0:</strong> {service.sub_category_l0}
                              </div>
                            )}
                            {service.sub_category_l1 && (
                              <div>
                                <strong>L1:</strong> {service.sub_category_l1}
                              </div>
                            )}
                            {service.sub_category_l2 && (
                              <div>
                                <strong>L2:</strong> {service.sub_category_l2}
                              </div>
                            )}
                            {service.sub_category_l3 && (
                              <div>
                                <strong>L3:</strong> {service.sub_category_l3}
                              </div>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Description *
                            </label>
                            <textarea
                              value={editServiceData.description}
                              onChange={(e) =>
                                setEditServiceData({
                                  ...editServiceData,
                                  description: e.target.value,
                                })
                              }
                              placeholder="Enter service description"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-red-500"
                              rows="2"
                              disabled={loading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Direct URL *
                            </label>
                            <input
                              type="url"
                              value={editServiceData.direct_url}
                              onChange={(e) =>
                                setEditServiceData({
                                  ...editServiceData,
                                  direct_url: e.target.value,
                                })
                              }
                              placeholder="https://example.com/service"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-red-500"
                              disabled={loading}
                            />
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={handleUpdateService}
                              className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 disabled:opacity-50"
                              disabled={loading}
                            >
                              Update
                            </button>
                            <button
                              onClick={handleCancelEditService}
                              className="px-2 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600"
                              disabled={loading}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View mode
                        <div className="flex justify-between items-start">
                          <div className="grid grid-cols-2 gap-2 flex-1">
                            <div>
                              <strong>ID:</strong> {service.id}
                            </div>
                            <div>
                              <strong>Category:</strong> {service.category}
                            </div>
                            {service.sub_category_l0 && (
                              <div>
                                <strong>L0:</strong> {service.sub_category_l0}
                              </div>
                            )}
                            {service.sub_category_l1 && (
                              <div>
                                <strong>L1:</strong> {service.sub_category_l1}
                              </div>
                            )}
                            {service.sub_category_l2 && (
                              <div>
                                <strong>L2:</strong> {service.sub_category_l2}
                              </div>
                            )}
                            {service.sub_category_l3 && (
                              <div>
                                <strong>L3:</strong> {service.sub_category_l3}
                              </div>
                            )}
                            {service.description && (
                              <div className="col-span-2">
                                <strong>Description:</strong>{" "}
                                {service.description}
                              </div>
                            )}
                            {service.direct_url && (
                              <div className="col-span-2">
                                <strong>URL:</strong>
                                <a
                                  href={service.direct_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-red-600 hover:text-red-800 underline ml-1"
                                >
                                  {service.direct_url}
                                </a>
                              </div>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleEditService(service)}
                              className="px-2 py-1 text-red-600 hover:bg-red-100 rounded text-sm flex items-center gap-1"
                              disabled={loading}
                              title="Edit this service"
                            >
                              <svg
                                className="w-3 h-3"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteService(service.id)}
                              className="px-2 py-1 text-red-600 hover:bg-red-100 rounded text-sm flex items-center gap-1"
                              disabled={loading}
                              title="Delete this service"
                            >
                              <svg
                                className="w-3 h-3"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {loading && (
        <div className="mt-4 text-center">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-red-500"></div>
          <span className="ml-2 text-gray-600">Loading...</span>
        </div>
      )}

      {/* Template Modal */}
      {showTemplate && templateData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                CSV Template for Bulk Upload
              </h2>
              <button
                onClick={() => setShowTemplate(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Instructions */}
              <div className="bg-red-50 p-4 rounded-md">
                <h3 className="font-medium text-red-800 mb-2">
                  Instructions:
                </h3>
                <ul className="list-disc list-inside text-red-700 text-sm space-y-1">
                  {templateData.instructions.map((instruction, index) => (
                    <li key={index}>{instruction}</li>
                  ))}
                </ul>
              </div>

              {/* Headers */}
              <div>
                <h3 className="font-medium text-gray-800 mb-2">CSV Headers:</h3>
                <div className="bg-gray-100 p-3 rounded-md">
                  <code className="text-sm">
                    {templateData.headers.join(", ")}
                  </code>
                </div>
              </div>

              {/* Sample Data */}
              <div>
                <h3 className="font-medium text-gray-800 mb-2">Sample Data:</h3>
                <div className="bg-gray-50 p-4 rounded-md overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        {templateData.headers.map((header, index) => (
                          <th
                            key={index}
                            className="text-left py-2 px-2 font-medium text-gray-700"
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {templateData.sample_rows.map((row, rowIndex) => (
                        <tr key={rowIndex} className="border-b">
                          {templateData.headers.map((header, colIndex) => (
                            <td
                              key={colIndex}
                              className="py-2 px-2 text-gray-600"
                            >
                              {row[header] || ""}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Download Button */}
              <div className="flex justify-center">
                <button
                  onClick={downloadCSVTemplate}
                  className="px-6 py-3 bg-red-500 text-white rounded-md hover:bg-red-600 flex items-center gap-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Download CSV Template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Action Bars */}
      <BulkActionBar
        selectedCount={selectedCategories.length}
        onDelete={() => openBulkDeleteConfirm("categories")}
        onCancel={() => cancelBulkSelection("categories")}
        itemType="category"
      />

      <BulkActionBar
        selectedCount={selectedSubcategories.length}
        onDelete={() => openBulkDeleteConfirm("subcategories")}
        onCancel={() => cancelBulkSelection("subcategories")}
        itemType="subcategory"
      />

      <BulkActionBar
        selectedCount={selectedDeletedServices.length}
        onDelete={() => openBulkDeleteConfirm("permanent")}
        onCancel={() => cancelBulkSelection("deleted")}
        itemType="deleted service"
      />

      {/* Bulk Delete Confirmation Dialog */}
      <BulkDeleteConfirmDialog
        isOpen={showBulkDeleteConfirm}
        onClose={cancelBulkDelete}
        onConfirm={confirmBulkDelete}
        items={
          bulkDeleteType === "categories"
            ? selectedCategories.map((cat) => ({ name: cat }))
            : bulkDeleteType === "subcategories"
            ? selectedSubcategories.map((sub) => ({
                name: `${sub.current_path.join(" > ")} > ${sub.subcategory_name}`,
              }))
            : selectedDeletedServices.map((id) => {
                const service = deletedServices.find((s) => s.id === id);
                return {
                  name: service
                    ? `${service.category}${service.sub_category_l0 ? ` > ${service.sub_category_l0}` : ""}${service.sub_category_l1 ? ` > ${service.sub_category_l1}` : ""}`
                    : `Service ID ${id}`,
                };
              })
        }
        summary={null}
        itemType={
          bulkDeleteType === "categories"
            ? "category"
            : bulkDeleteType === "subcategories"
            ? "subcategory"
            : bulkDeleteType === "restore"
            ? "service"
            : "service"
        }
        isLoading={bulkDeleting}
      />
    </div>
  );
};

export default AdminPanel;
